import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, TextField, InputAdornment, IconButton,
  CircularProgress, Chip, ToggleButtonGroup, ToggleButton, FormControlLabel, Checkbox,
  Autocomplete, Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import { createChart, IChartApi, LineStyle, UTCTimestamp } from 'lightweight-charts';
import { bloomberg } from '../theme/bloombergTheme';
import { useThemeMode } from '../theme/ThemeContext';
import { formatPrice as formatCurrencyPrice, getCurrencyFromTicker } from '../utils/formatCurrency';
import {
  getTechnicalChartData, TechnicalChartPoint, TechnicalEvent, ElliottWave,
  searchCorrelationSymbols, getRsChart, listRsPairs, saveRsPair, deleteRsPair,
  getChartingQuotes, ChartingQuote, getPatterns, ChartPatternRow,
  CorrelationSearchResult, RsChartResponse, RsSavedPair,
} from '../services/api';

// ──────────────────────────────────────────────────────────────────────────
// Charting — TradingView-style layout: chart on the left, a right-hand panel
// listing saved pairs and standalone stocks. Two modes share the page:
//  - Stock: single-symbol technical chart (ported from the old Analysis
//    page — MAs, Bollinger Bands, MACD, Stochastic, VWAP, OBV, S/R, Elliott
//    Wave, comparison overlay). Fibonacci is deliberately not carried over —
//    it already lives in Model Portfolio.
//  - Pair: relative-strength ratio chart (A/B indexed to 100) with
//    chart_annotator's auto S/R levels & trendlines. Empty overlays are
//    expected on a one-way blowout pair, not a bug.
// The old ticker technical-indicator table and Efficient Frontier section
// were dropped rather than ported — neither is a charting concern.
// ──────────────────────────────────────────────────────────────────────────

// Shared with the (currently unused) EfficientFrontier component's storage —
// kept as the watchlist key so existing saved tickers aren't lost.
const WATCHLIST_KEY = 'analytics_tickers';

const loadWatchlist = (): string[] => {
  try {
    const stored = localStorage.getItem(WATCHLIST_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveWatchlist = (tickers: string[]) => {
  localStorage.setItem(WATCHLIST_KEY, JSON.stringify(tickers));
};

// ── Terminal Grid theme — sharp corners, hairlines, dense mono tables,
// instead of the app's usual rounded card-dashboard chrome. Backgrounds,
// borders, text and the accent all resolve through the app's own bloomberg.*
// CSS-variable tokens, so this page follows the system light/dark toggle
// exactly like every other page (amber accent in dark, blue in light).
// Scoped entirely to this file via inline sx; no other page is affected. ──
const T_BG = bloomberg.bg.primary;
const T_PANEL = bloomberg.bg.secondary;
const T_BORDER = bloomberg.border.secondary;
const T_BORDER_DIM = bloomberg.border.primary;
const T_ROW_HOVER = bloomberg.bg.hover;
const T_ROW_SELECTED = bloomberg.accent.orangeDim;
const T_AMBER = bloomberg.accent.orange;
const T_AMBER_DIM = bloomberg.accent.orangeDim;
const T_LABEL = bloomberg.text.muted;
const T_TEXT = bloomberg.text.secondary;
const MONO = '"IBM Plex Mono","SF Mono","Roboto Mono",Consolas,monospace';

const termFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0, bgcolor: T_PANEL, fontFamily: MONO, fontSize: 12.5, color: T_AMBER,
    '& fieldset': { borderColor: T_BORDER },
    '&:hover fieldset': { borderColor: T_LABEL },
    '&.Mui-focused fieldset': { borderColor: T_AMBER, borderWidth: 1 },
  },
  '& .MuiInputLabel-root': { fontFamily: MONO, fontSize: 12, color: T_LABEL, '&.Mui-focused': { color: T_AMBER } },
  '& input::placeholder': { color: T_LABEL, opacity: 1 },
};

const termToggleGroupSx = {
  bgcolor: T_PANEL, border: `1px solid ${T_BORDER}`, borderRadius: 0,
  '& .MuiToggleButton-root': {
    fontFamily: MONO, fontSize: 11, color: T_LABEL, border: 'none', borderRadius: 0,
    borderLeft: `1px solid ${T_BORDER}`, padding: '5px 10px',
    '&:first-of-type': { borderLeft: 'none' },
    '&.Mui-selected': { bgcolor: T_AMBER, color: '#fff', fontWeight: 700, '&:hover': { bgcolor: T_AMBER } },
    '&:hover': { bgcolor: T_ROW_HOVER, color: T_AMBER },
  },
};

const termIconBtnSx = {
  borderRadius: 0, color: T_LABEL, border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL,
  '&:hover': { color: T_AMBER, bgcolor: T_ROW_HOVER, borderColor: T_LABEL },
  '&.Mui-disabled': { color: T_BORDER },
};

const RS_TIMEFRAMES = [
  { value: '10D', label: '10D' }, { value: '20D', label: '20D' }, { value: '30D', label: '30D' },
  { value: '1M', label: '1M' }, { value: '3M', label: '3M' }, { value: '6M', label: '6M' }, { value: '1Y', label: '1Y' },
];

type Mode = 'stock' | 'pair';

const Charting: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);
  const [mode, setMode] = useState<Mode>('stock');

  // ── Theme-aware chart colors ───────────────────────────────────────────
  // lightweight-charts renders to <canvas> and cannot resolve CSS variables
  // (bloomberg.* tokens), so anything drawn on a chart needs a real literal
  // hex picked per mode — everything else on this page just uses the tokens
  // above directly and lets the browser handle it.
  const { mode: themeMode } = useThemeMode();
  const isDarkTheme = themeMode === 'dark';
  const GREEN = isDarkTheme ? '#5eff8f' : '#16a34a';
  const RED = isDarkTheme ? '#ff5e5e' : '#dc2626';
  const CHART_ORANGE = isDarkTheme ? '#ff8c00' : '#0016e0';
  const CHART_MUTED = isDarkTheme ? '#64748b' : '#718096';
  const CHART_MUTED_DIM = isDarkTheme ? '#334155' : '#cbd5e1';
  const CHART_BORDER = isDarkTheme ? '#1e293b' : '#e2e8f0';
  const CHART_GRID = isDarkTheme ? 'rgba(148,163,184,0.07)' : 'rgba(71,85,105,0.10)';
  const CHART_CROSSHAIR_BG = CHART_BORDER;
  const GREEN_BG = isDarkTheme ? 'rgba(94,255,143,0.08)' : 'rgba(22,163,74,0.08)';
  const RED_BG = isDarkTheme ? 'rgba(255,94,94,0.08)' : 'rgba(220,38,38,0.08)';

  // ── Watchlist (standalone stocks) ─────────────────────────────────────
  const [watchlist, setWatchlist] = useState<string[]>(loadWatchlist);
  const [watchlistInput, setWatchlistInput] = useState('');

  useEffect(() => { saveWatchlist(watchlist); }, [watchlist]);

  const handleAddToWatchlist = (symbolRaw?: string) => {
    const ticker = (symbolRaw ?? watchlistInput).trim().toUpperCase();
    if (ticker && !watchlist.includes(ticker)) {
      setWatchlist([...watchlist, ticker]);
    }
    setWatchlistInput('');
  };

  const handleRemoveFromWatchlist = (ticker: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWatchlist(watchlist.filter((t) => t !== ticker));
  };

  // ── Sidebar quotes: last price + 1D % change for watchlist stocks and
  // saved-pair legs, from the bulk cached-close endpoint. ──────────────
  const [quotes, setQuotes] = useState<Record<string, ChartingQuote>>({});

  // ── Stock mode state (ported from the old Analysis page) ─────────────
  const [chartSearchValue, setChartSearchValue] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [chartData, setChartData] = useState<TechnicalChartPoint[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartPeriod, setChartPeriod] = useState<string>('1y');
  const [chartInterval, setChartInterval] = useState<string>('1d');
  const [technicalEvents, setTechnicalEvents] = useState<TechnicalEvent[]>([]);
  const [elliottWaves, setElliottWaves] = useState<ElliottWave[]>([]);
  const [patternRow, setPatternRow] = useState<ChartPatternRow | null>(null);
  const [hoverInfo, setHoverInfo] = useState<TechnicalChartPoint | null>(null);

  const mainChartContainerRef = useRef<HTMLDivElement>(null);
  const rsiContainerRef = useRef<HTMLDivElement>(null);
  const macdContainerRef = useRef<HTMLDivElement>(null);
  const stochContainerRef = useRef<HTMLDivElement>(null);
  const obvContainerRef = useRef<HTMLDivElement>(null);
  const mainChartApiRef = useRef<IChartApi | null>(null);

  const [comparisonSymbols, setComparisonSymbols] = useState<string[]>([]);
  const [comparisonData, setComparisonData] = useState<Map<string, TechnicalChartPoint[]>>(new Map());
  const [comparisonNames, setComparisonNames] = useState<Map<string, string>>(new Map());
  const [comparisonInput, setComparisonInput] = useState('');

  const [showMA20, setShowMA20] = useState(true);
  const [showMA50, setShowMA50] = useState(true);
  const [showMA200, setShowMA200] = useState(true);
  const [showBB, setShowBB] = useState(false);
  const [showElliott, setShowElliott] = useState(false);
  const [showMACD, setShowMACD] = useState(false);
  const [showStochastic, setShowStochastic] = useState(false);
  const [showVWAP, setShowVWAP] = useState(false);
  const [showOBV, setShowOBV] = useState(false);
  const [showSupportResistance, setShowSupportResistance] = useState(false);

  const fetchChartData = useCallback(async () => {
    if (!selectedSymbol) {
      setChartData([]); setTechnicalEvents([]); setElliottWaves([]); setCompanyName(null);
      return;
    }
    setChartLoading(true);
    try {
      const response = await getTechnicalChartData(selectedSymbol, chartPeriod);
      setChartData(response.data || []);
      setChartInterval(response.interval || '1d');
      setTechnicalEvents(response.events || []);
      setElliottWaves(response.elliott_waves || []);
      setCompanyName(response.company_name || null);
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setChartData([]); setChartInterval('1d'); setTechnicalEvents([]); setElliottWaves([]); setCompanyName(null);
    } finally {
      setChartLoading(false);
    }
  }, [selectedSymbol, chartPeriod]);

  useEffect(() => { fetchChartData(); }, [fetchChartData]);

  // Any stored chart pattern for this symbol (from the periodically-scanned
  // universe) — overlaid on the chart when present, honest-blank otherwise.
  useEffect(() => {
    if (!selectedSymbol) { setPatternRow(null); return; }
    let cancelled = false;
    getPatterns({ symbol: selectedSymbol })
      .then((res) => { if (!cancelled) setPatternRow(res.patterns[0] || null); })
      .catch((e) => { console.error('Error loading pattern:', e); if (!cancelled) setPatternRow(null); });
    return () => { cancelled = true; };
  }, [selectedSymbol]);

  // Deep-link: ?symbol=X (and optional &mode=stock|pair) from other pages
  useEffect(() => {
    const symbolParam = searchParams.get('symbol');
    const modeParam = searchParams.get('mode');
    if (symbolParam && !deepLinkHandled.current) {
      deepLinkHandled.current = true;
      const sym = symbolParam.toUpperCase();
      setMode(modeParam === 'pair' ? 'pair' : 'stock');
      setSelectedSymbol(sym);
      setChartSearchValue(sym);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleChartSearch = () => {
    const ticker = chartSearchValue.trim().toUpperCase();
    if (ticker) setSelectedSymbol(ticker);
  };

  const fetchComparisonChartData = useCallback(async (symbol: string) => {
    try {
      const response = await getTechnicalChartData(symbol, chartPeriod);
      if (response.data && response.data.length > 0) {
        setComparisonData((prev) => new Map(prev).set(symbol, response.data));
        if (response.company_name) {
          setComparisonNames((prev) => new Map(prev).set(symbol, response.company_name || symbol));
        }
      }
    } catch (error) {
      console.error(`Error fetching comparison data for ${symbol}:`, error);
    }
  }, [chartPeriod]);

  const handleAddComparison = () => {
    const ticker = comparisonInput.trim().toUpperCase();
    if (ticker && ticker !== selectedSymbol && !comparisonSymbols.includes(ticker)) {
      setComparisonSymbols([...comparisonSymbols, ticker]);
      fetchComparisonChartData(ticker);
      setComparisonInput('');
    }
  };

  const handleRemoveComparison = (symbol: string) => {
    setComparisonSymbols(comparisonSymbols.filter((s) => s !== symbol));
    setComparisonData((prev) => { const m = new Map(prev); m.delete(symbol); return m; });
    setComparisonNames((prev) => { const m = new Map(prev); m.delete(symbol); return m; });
  };

  useEffect(() => {
    if (comparisonSymbols.length > 0) {
      comparisonSymbols.forEach((symbol) => fetchComparisonChartData(symbol));
    }
  }, [chartPeriod, comparisonSymbols, fetchComparisonChartData]);

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: string | null) => {
    if (newPeriod) setChartPeriod(newPeriod);
  };

  const isIntraday = ['5m', '15m', '1h'].includes(chartInterval);

  const formatChartLabel = (label: string, short = false) => {
    const d = new Date(label.replace(' ', 'T'));
    if (isIntraday) {
      if (short) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Pattern-detection Edge grade — same thresholds as the Patterns page.
  const edgeGrade = (ev: number | null): { g: string; color: string } | null => {
    if (ev == null) return null;
    if (ev >= 0.3) return { g: 'A', color: GREEN };
    if (ev >= 0.15) return { g: 'B', color: '#a3e635' };
    if (ev >= 0) return { g: 'C', color: CHART_MUTED };
    return { g: 'D', color: RED };
  };

  // ── Pair mode state (Relative Strength) ───────────────────────────────
  const [symbolAInput, setSymbolAInput] = useState('');
  const [symbolBInput, setSymbolBInput] = useState('');
  const [searchAResults, setSearchAResults] = useState<CorrelationSearchResult[]>([]);
  const [searchBResults, setSearchBResults] = useState<CorrelationSearchResult[]>([]);
  const [symbolA, setSymbolA] = useState<string | null>(null);
  const [symbolB, setSymbolB] = useState<string | null>(null);
  const [rsTimeframe, setRsTimeframe] = useState('1Y');

  const [rsData, setRsData] = useState<RsChartResponse | null>(null);
  const [rsLoading, setRsLoading] = useState(false);
  const [rsError, setRsError] = useState<string | null>(null);

  const [savedPairs, setSavedPairs] = useState<RsSavedPair[]>([]);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [savingPair, setSavingPair] = useState(false);

  const rsChartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const symbols = new Set<string>(watchlist);
    savedPairs.forEach((p) => { symbols.add(p.symbol_a); symbols.add(p.symbol_b); });
    // Also include the currently-plotted pair's legs — otherwise a pair that
    // hasn't been saved yet (or isn't in the watchlist) never gets its LAST
    // price fetched, and the stat strip shows "—" until you save it.
    if (rsData) { symbols.add(rsData.symbol_a); symbols.add(rsData.symbol_b); }
    if (symbols.size === 0) { setQuotes({}); return; }
    let cancelled = false;
    getChartingQuotes(Array.from(symbols))
      .then((res) => { if (!cancelled) setQuotes(res.quotes); })
      .catch((e) => console.error('Error loading sidebar quotes:', e));
    return () => { cancelled = true; };
  }, [watchlist, savedPairs, rsData?.symbol_a, rsData?.symbol_b]);

  const refreshSavedPairs = useCallback(async () => {
    setPairsLoading(true);
    try {
      const result = await listRsPairs();
      setSavedPairs(result.pairs);
    } catch (e) {
      console.error('Error loading saved pairs:', e);
    } finally {
      setPairsLoading(false);
    }
  }, []);

  useEffect(() => { refreshSavedPairs(); }, [refreshSavedPairs]);

  useEffect(() => {
    if (symbolAInput.length < 1) { setSearchAResults([]); return; }
    const t = setTimeout(async () => {
      try { setSearchAResults((await searchCorrelationSymbols(symbolAInput, 10)).results); } catch (e) { console.error(e); }
    }, 300);
    return () => clearTimeout(t);
  }, [symbolAInput]);

  useEffect(() => {
    if (symbolBInput.length < 1) { setSearchBResults([]); return; }
    const t = setTimeout(async () => {
      try { setSearchBResults((await searchCorrelationSymbols(symbolBInput, 10)).results); } catch (e) { console.error(e); }
    }, 300);
    return () => clearTimeout(t);
  }, [symbolBInput]);

  const loadPair = useCallback(async (a: string, b: string, tf: string) => {
    if (!a || !b) return;
    setRsLoading(true); setRsError(null);
    try {
      const data = await getRsChart(a, b, tf);
      if (data.error) { setRsError(data.error); setRsData(null); }
      else { setRsData(data); }
    } catch (e) {
      console.error('Error loading RS chart:', e);
      setRsError('Failed to load relative strength chart'); setRsData(null);
    } finally {
      setRsLoading(false);
    }
  }, []);

  const handlePlotPair = () => {
    if (!symbolA || !symbolB) { setRsError('Select two symbols to compare'); return; }
    if (symbolA === symbolB) { setRsError('Choose two different symbols'); return; }
    loadPair(symbolA, symbolB, rsTimeframe);
  };

  const handleSelectSavedPair = (pair: RsSavedPair) => {
    setMode('pair');
    setSymbolA(pair.symbol_a); setSymbolB(pair.symbol_b);
    setSymbolAInput(pair.symbol_a); setSymbolBInput(pair.symbol_b);
    loadPair(pair.symbol_a, pair.symbol_b, rsTimeframe);
  };

  const handleSelectWatchlistStock = (symbol: string) => {
    setMode('stock');
    setSelectedSymbol(symbol);
    setChartSearchValue(symbol);
  };

  const handleSavePair = async () => {
    if (!rsData) return;
    setSavingPair(true);
    try { await saveRsPair(rsData.symbol_a, rsData.symbol_b); await refreshSavedPairs(); }
    catch (e) { console.error('Error saving pair:', e); }
    finally { setSavingPair(false); }
  };

  const handleDeletePair = async (pairId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try { await deleteRsPair(pairId); setSavedPairs((prev) => prev.filter((p) => p.id !== pairId)); }
    catch (e) { console.error('Error deleting pair:', e); }
  };

  useEffect(() => {
    if (symbolA && symbolB) loadPair(symbolA, symbolB, rsTimeframe);
  }, [rsTimeframe, symbolA, symbolB, loadPair]);

  useEffect(() => {
    if (!rsData || !rsData.series?.length || !rsChartRef.current) return;
    let chart: IChartApi | null = null;
    const series = rsData.series.map((d) => ({ time: d.date, value: d.value }));

    chart = createChart(rsChartRef.current, {
      height: 460,
      autoSize: true,
      layout: { background: { color: 'transparent' }, textColor: CHART_MUTED, fontSize: 10 },
      grid: { vertLines: { color: CHART_GRID }, horzLines: { color: CHART_GRID } },
      rightPriceScale: { borderColor: CHART_BORDER },
      timeScale: { borderColor: CHART_BORDER },
      crosshair: { horzLine: { labelBackgroundColor: CHART_CROSSHAIR_BG }, vertLine: { labelBackgroundColor: CHART_CROSSHAIR_BG } },
    });

    const winning = rsData.winning_symbol;
    const lineColor = winning === rsData.symbol_a ? GREEN : winning === rsData.symbol_b ? RED : CHART_ORANGE;

    const rsLine = chart.addLineSeries({ color: lineColor, lineWidth: 2, priceLineVisible: false, lastValueVisible: true });
    rsLine.setData(series);
    rsLine.createPriceLine({ price: 100, color: CHART_MUTED_DIM, lineStyle: LineStyle.Dashed, lineWidth: 1, title: 'even' });

    const knownDates = new Set(series.map((d) => d.time));

    (rsData.annotations?.levels || []).forEach((lvl) => {
      rsLine.createPriceLine({
        price: lvl.price, color: lvl.kind === 'resistance' ? RED : GREEN, lineStyle: LineStyle.Dotted, lineWidth: 1,
        title: `${lvl.kind[0].toUpperCase()}${lvl.kind.slice(1)} (${lvl.touches}x)`,
      });
    });

    (rsData.annotations?.trendlines || []).forEach((tl) => {
      const pts = tl.points.filter((p) => knownDates.has(p.date)).map((p) => ({ time: p.date, value: p.price }));
      if (pts.length < 2) return;
      const ls = chart!.addLineSeries({
        color: tl.kind === 'resistance' ? RED : GREEN, lineWidth: 2, lineStyle: LineStyle.Dashed,
        priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
      });
      ls.setData(pts.sort((a, b) => (a.time < b.time ? -1 : 1)));
    });

    (rsData.annotations?.channels || []).forEach((ch) => {
      [ch.upper, ch.lower].forEach((side) => {
        const pts = side.filter((p) => knownDates.has(p.date)).map((p) => ({ time: p.date, value: p.price }));
        if (pts.length < 2) return;
        const ls = chart!.addLineSeries({
          color: CHART_MUTED, lineWidth: 1, lineStyle: LineStyle.Dashed,
          priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        });
        ls.setData(pts.sort((a, b) => (a.time < b.time ? -1 : 1)));
      });
    });

    chart.timeScale().fitContent();
    return () => { if (chart) chart.remove(); };
  }, [rsData, isDarkTheme]);

  const isPairSaved = rsData && savedPairs.some((p) => p.symbol_a === rsData.symbol_a && p.symbol_b === rsData.symbol_b);

  // ── Stock mode chart (lightweight-charts): candles + overlays + pattern
  // geometry + synced RSI/MACD/Stochastic/OBV subplots. Rebuilt from scratch
  // on any dependency change, same "recreate the whole chart" approach as
  // Patterns.tsx's PatternChart — data sizes here (a few hundred bars) make
  // that cheap, and it sidesteps lightweight-charts' update-in-place quirks.
  useEffect(() => {
    if (mode !== 'stock' || !chartData.length || !mainChartContainerRef.current) return;

    const charts: IChartApi[] = [];
    const isComparison = comparisonSymbols.length > 0;

    // Always numeric UTCTimestamp seconds — avoids lightweight-charts'
    // BusinessDay-object round-trip ambiguity on crosshair-move lookups.
    const toTime = (d: string): UTCTimestamp =>
      Math.floor(new Date(d.includes(' ') ? d.replace(' ', 'T') : `${d}T00:00:00`).getTime() / 1000) as UTCTimestamp;

    const commonOptions = (height: number, interactive: boolean) => ({
      height,
      autoSize: true,
      handleScroll: interactive, handleScale: interactive,
      layout: { background: { color: 'transparent' }, textColor: CHART_MUTED, fontSize: 10 },
      grid: { vertLines: { color: CHART_GRID }, horzLines: { color: CHART_GRID } },
      rightPriceScale: { borderColor: CHART_BORDER },
      timeScale: { borderColor: CHART_BORDER, timeVisible: isIntraday, visible: interactive },
      crosshair: { horzLine: { labelBackgroundColor: CHART_CROSSHAIR_BG }, vertLine: { labelBackgroundColor: CHART_CROSSHAIR_BG } },
    });

    const mainChart = createChart(mainChartContainerRef.current, commonOptions(400, true));
    charts.push(mainChart);
    mainChartApiRef.current = mainChart;

    let priceSeries: any;

    if (isComparison) {
      const firstMainPrice = chartData[0]?.price || null;
      priceSeries = mainChart.addLineSeries({ color: '#ffffff', lineWidth: 2 });
      priceSeries.setData(
        chartData.filter((d) => d.price != null).map((d) => ({
          time: toTime(d.date),
          value: firstMainPrice && firstMainPrice > 0 ? ((d.price - firstMainPrice) / firstMainPrice) * 100 : 0,
        }))
      );
      const colors = ['#f44336', '#4caf50', '#ff9800', '#c084fc', '#22d3ee', '#f472b6'];
      comparisonSymbols.forEach((symbol, index) => {
        const d = comparisonData.get(symbol);
        const firstCompPrice = d && d.length > 0 ? d[0]?.price : null;
        if (!d || !firstCompPrice) return;
        const compSeries = mainChart.addLineSeries({
          color: colors[index % colors.length], lineWidth: 2,
          lineStyle: index % 2 === 0 ? LineStyle.Dashed : LineStyle.Solid,
        });
        compSeries.setData(d.filter((p) => p.price != null).map((p) => ({
          time: toTime(p.date), value: ((p.price - firstCompPrice) / firstCompPrice) * 100,
        })));
      });
    } else {
      priceSeries = mainChart.addCandlestickSeries({
        upColor: GREEN, downColor: RED, borderVisible: false, wickUpColor: GREEN, wickDownColor: RED,
      });
      priceSeries.setData(
        chartData
          .filter((d) => d.open != null && d.high != null && d.low != null && d.price != null)
          .map((d) => ({ time: toTime(d.date), open: d.open, high: d.high, low: d.low, close: d.price }))
      );

      const addOverlayLine = (key: keyof TechnicalChartPoint, color: string, width: 1 | 2, dashed?: boolean) => {
        const data = chartData.filter((d) => (d as any)[key] != null).map((d) => ({ time: toTime(d.date), value: (d as any)[key] as number }));
        if (!data.length) return;
        const s = mainChart.addLineSeries({
          color, lineWidth: width, lineStyle: dashed ? LineStyle.Dashed : LineStyle.Solid,
          priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        });
        s.setData(data);
      };

      if (showMA20) addOverlayLine('ma_20', '#ff9800', 1);
      if (showMA50) addOverlayLine('ma_50', '#f44336', 1);
      if (showMA200) addOverlayLine('ma_200', '#3b6ea5', 1);
      if (showVWAP) addOverlayLine('vwap', '#22c55e', 2, true);
      if (showBB) {
        // lightweight-charts renders to <canvas> and cannot resolve CSS
        // variables like bloomberg.* tokens — must be a literal color here.
        addOverlayLine('bb_upper', CHART_ORANGE, 1, true);
        addOverlayLine('bb_lower', CHART_ORANGE, 1, true);
      }

      if (showSupportResistance) {
        const last = chartData[chartData.length - 1];
        if (last?.resistance_1 != null) priceSeries.createPriceLine({ price: last.resistance_1, color: '#ef4444', lineStyle: LineStyle.Dashed, lineWidth: 1, title: 'R1' });
        if (last?.support_1 != null) priceSeries.createPriceLine({ price: last.support_1, color: '#22c55e', lineStyle: LineStyle.Dashed, lineWidth: 1, title: 'S1' });
        if (last?.pivot != null) priceSeries.createPriceLine({ price: last.pivot, color: CHART_MUTED, lineStyle: LineStyle.Dotted, lineWidth: 1, title: 'Pivot' });
      }

      // Pattern overlay — same geometry-drawing approach as Patterns.tsx's
      // PatternChart, just fed by a symbol lookup instead of a passed-in row.
      if (patternRow) {
        const pc = patternRow.direction === 'bullish' ? GREEN : RED;
        (patternRow.geometry?.lines || []).forEach((line) => {
          const pts = line.points.map((p) => ({ time: toTime(p.date), value: p.price })).sort((a, b) => a.time - b.time);
          if (pts.length < 2) return;
          const ls = mainChart.addLineSeries({
            color: pc, lineWidth: 2, lineStyle: LineStyle.Dashed,
            priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
          });
          ls.setData(pts);
        });
        priceSeries.createPriceLine({ price: patternRow.target_price, color: pc, lineStyle: LineStyle.Dotted, lineWidth: 1, title: `target ${patternRow.target_price}` });
        priceSeries.createPriceLine({ price: patternRow.neckline_price, color: CHART_MUTED_DIM, lineStyle: LineStyle.Dashed, lineWidth: 1, title: 'breakout' });
      }
    }

    // Markers: Elliott wave labels + golden/death cross + pattern pivots —
    // merged into one setMarkers call (lightweight-charts allows only one
    // active marker set per series).
    const knownTimes = new Set(chartData.map((d) => toTime(d.date)));
    const markers: any[] = [];
    if (showElliott) {
      elliottWaves.forEach((wave) => {
        const t = toTime(wave.date);
        if (!knownTimes.has(t)) return;
        markers.push({
          time: t, position: wave.swing_type === 'high' ? 'aboveBar' : 'belowBar',
          color: wave.type === 'impulse' ? '#22d3ee' : '#ff5722', shape: 'circle', text: wave.label,
        });
      });
    }
    technicalEvents.filter((e) => e.type === 'golden_cross' || e.type === 'death_cross').forEach((event) => {
      const t = toTime(event.date);
      if (!knownTimes.has(t)) return;
      const isGolden = event.type === 'golden_cross';
      markers.push({
        time: t, position: 'aboveBar', color: isGolden ? GREEN : RED,
        shape: 'circle', text: isGolden ? '⭐' : '⚠️',
      });
    });
    if (!isComparison && patternRow) {
      const pc = patternRow.direction === 'bullish' ? GREEN : RED;
      (patternRow.geometry?.pivots || []).forEach((pv) => {
        const t = toTime(pv.date);
        if (!knownTimes.has(t)) return;
        markers.push({ time: t, position: 'inBar', color: pc, shape: 'circle', size: 1 });
      });
    }
    if (markers.length) priceSeries.setMarkers(markers.sort((a, b) => a.time - b.time));

    // Hover info bar (replaces the old Recharts tooltip)
    const timeToRow = new Map<number, TechnicalChartPoint>(chartData.map((d) => [toTime(d.date), d]));
    mainChart.subscribeCrosshairMove((param) => {
      if (param.time == null) { setHoverInfo(null); return; }
      setHoverInfo(timeToRow.get(param.time as unknown as number) || null);
    });

    // Subplots — locked to the main chart's time scale (no independent zoom).
    const buildSubplot = (container: HTMLDivElement | null, height: number, build: (c: IChartApi) => void) => {
      if (!container) return;
      const c = createChart(container, commonOptions(height, false));
      build(c);
      charts.push(c);
    };

    if (chartData.some((d) => d.rsi != null)) {
      buildSubplot(rsiContainerRef.current, 120, (c) => {
        const s = c.addLineSeries({ color: '#c084fc', lineWidth: 2 });
        s.setData(chartData.filter((d) => d.rsi != null).map((d) => ({ time: toTime(d.date), value: d.rsi as number })));
        s.createPriceLine({ price: 70, color: '#f44336', lineStyle: LineStyle.Dashed, lineWidth: 1, title: '70' });
        s.createPriceLine({ price: 30, color: '#4caf50', lineStyle: LineStyle.Dashed, lineWidth: 1, title: '30' });
      });
    }

    if (showMACD && chartData.some((d) => d.macd != null)) {
      buildSubplot(macdContainerRef.current, 150, (c) => {
        const macdLine = c.addLineSeries({ color: '#60a5fa', lineWidth: 2 });
        macdLine.setData(chartData.filter((d) => d.macd != null).map((d) => ({ time: toTime(d.date), value: d.macd as number })));
        const signalLine = c.addLineSeries({ color: '#ff9800', lineWidth: 1 });
        signalLine.setData(chartData.filter((d) => d.macd_signal != null).map((d) => ({ time: toTime(d.date), value: d.macd_signal as number })));
        const hist = c.addHistogramSeries({ color: '#4caf50' });
        hist.setData(chartData.filter((d) => d.macd_histogram != null).map((d) => ({
          time: toTime(d.date), value: d.macd_histogram as number,
          color: (d.macd_histogram as number) >= 0 ? 'rgba(76,175,80,0.5)' : 'rgba(244,67,54,0.5)',
        })));
      });
    }

    if (showStochastic && chartData.some((d) => d.stoch_k != null)) {
      buildSubplot(stochContainerRef.current, 150, (c) => {
        const k = c.addLineSeries({ color: '#c084fc', lineWidth: 2 });
        k.setData(chartData.filter((d) => d.stoch_k != null).map((d) => ({ time: toTime(d.date), value: d.stoch_k as number })));
        const dLine = c.addLineSeries({ color: '#ff9800', lineWidth: 1 });
        dLine.setData(chartData.filter((d) => d.stoch_d != null).map((d) => ({ time: toTime(d.date), value: d.stoch_d as number })));
        k.createPriceLine({ price: 80, color: '#f44336', lineStyle: LineStyle.Dashed, lineWidth: 1, title: '80' });
        k.createPriceLine({ price: 20, color: '#4caf50', lineStyle: LineStyle.Dashed, lineWidth: 1, title: '20' });
      });
    }

    if (showOBV && chartData.some((d) => d.obv != null)) {
      buildSubplot(obvContainerRef.current, 150, (c) => {
        const s = c.addAreaSeries({ lineColor: '#ff9800', topColor: 'rgba(255,152,0,0.3)', bottomColor: 'rgba(255,152,0,0)', lineWidth: 2 });
        s.setData(chartData.filter((d) => d.obv != null).map((d) => ({ time: toTime(d.date), value: d.obv as number })));
      });
    }

    mainChart.timeScale().fitContent();

    const subplots = charts.slice(1);
    if (subplots.length > 0) {
      const initialRange = mainChart.timeScale().getVisibleLogicalRange();
      if (initialRange) subplots.forEach((c) => c.timeScale().setVisibleLogicalRange(initialRange));
      mainChart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
        if (!range) return;
        subplots.forEach((c) => { try { c.timeScale().setVisibleLogicalRange(range); } catch { /* disposed */ } });
      });
    }

    return () => {
      charts.forEach((c) => c.remove());
      mainChartApiRef.current = null;
    };
  }, [
    mode, chartData, comparisonSymbols, comparisonData, patternRow,
    showMA20, showMA50, showMA200, showBB, showVWAP, showSupportResistance,
    showElliott, showMACD, showStochastic, showOBV,
    technicalEvents, elliottWaves, isIntraday, isDarkTheme,
  ]);

  // ── Quote-strip derived values ────────────────────────────────────────
  const stockLastPoint = chartData.length > 0 ? chartData[chartData.length - 1] : null;
  const stockPrevPoint = chartData.length > 1 ? chartData[chartData.length - 2] : null;
  const stockChangePct = stockLastPoint && stockPrevPoint && stockPrevPoint.price
    ? ((stockLastPoint.price - stockPrevPoint.price) / stockPrevPoint.price) * 100
    : null;
  const patternGrade = patternRow ? edgeGrade(patternRow.expected_r) : null;
  const patternLabel = patternRow
    ? patternRow.pattern_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  const quoteA = rsData ? quotes[rsData.symbol_a] : null;
  const quoteB = rsData ? quotes[rsData.symbol_b] : null;
  const rsDeltaSinceStart = rsData?.current_value != null ? rsData.current_value - 100 : null;

  return (
    <Box sx={{ bgcolor: T_BG, border: `1px solid ${T_BORDER}`, color: T_TEXT, fontFamily: MONO }}>
      {/* ── Function bar ─────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'stretch', height: 34, borderBottom: `1px solid ${T_BORDER}`, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 2, borderRight: `1px solid ${T_BORDER_DIM}`, fontSize: 12, color: T_AMBER, letterSpacing: '0.02em' }}>
          CHARTING
        </Box>
        {(['stock', 'pair'] as Mode[]).map((m) => (
          <Box
            key={m}
            onClick={() => setMode(m)}
            sx={{
              display: 'flex', alignItems: 'center', px: 2, cursor: 'pointer', fontSize: 12,
              borderRight: `1px solid ${T_BORDER_DIM}`, letterSpacing: '0.02em',
              color: mode === m ? T_AMBER : T_LABEL,
              bgcolor: mode === m ? T_ROW_SELECTED : 'transparent',
              '&:hover': { color: T_AMBER },
            }}
          >
            {m.toUpperCase()}
          </Box>
        ))}
      </Box>

      {/* ── Quote strip ──────────────────────────────────────────────── */}
      {mode === 'pair' ? (
        rsData ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, py: 1.75, px: 2, borderBottom: `1px solid ${T_BORDER}` }}>
            {[
              ['PAIR', `${rsData.symbol_a}/${rsData.symbol_b}`, T_AMBER],
              ['RATIO', rsData.current_value != null ? rsData.current_value.toFixed(3) : '—', T_AMBER],
              ['Δ SINCE START', rsDeltaSinceStart != null ? `${rsDeltaSinceStart >= 0 ? '+' : ''}${rsDeltaSinceStart.toFixed(2)}%` : '—', rsDeltaSinceStart != null && rsDeltaSinceStart >= 0 ? GREEN : RED],
              ['WINNING', rsData.winning_symbol || '—', GREEN],
              [`${rsData.symbol_a} LAST`, quoteA?.price != null ? formatCurrencyPrice(quoteA.price, getCurrencyFromTicker(rsData.symbol_a)) : '—', T_AMBER],
              [`${rsData.symbol_b} LAST`, quoteB?.price != null ? formatCurrencyPrice(quoteB.price, getCurrencyFromTicker(rsData.symbol_b)) : '—', T_AMBER],
            ].map(([lbl, val, color], i) => (
              <Box key={lbl} sx={{ px: 2.75, borderRight: i < 5 ? `1px solid ${T_BORDER_DIM}` : 'none', '&:first-of-type': { pl: 0 } }}>
                <Typography sx={{ fontSize: 9.5, color: T_LABEL, letterSpacing: '0.1em', mb: 0.5 }}>{lbl}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color, fontFamily: MONO }}>{val}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ py: 1.75, px: 2, borderBottom: `1px solid ${T_BORDER}` }}>
            <Typography sx={{ fontSize: 12, color: T_LABEL }}>No pair plotted — choose Symbol A / Symbol B below.</Typography>
          </Box>
        )
      ) : (
        selectedSymbol && chartData.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, py: 1.75, px: 2, borderBottom: `1px solid ${T_BORDER}` }}>
            {[
              ['SYMBOL', companyName ? `${selectedSymbol}` : selectedSymbol, T_AMBER],
              ['LAST', stockLastPoint ? formatCurrencyPrice(stockLastPoint.price, getCurrencyFromTicker(selectedSymbol)) : '—', T_AMBER],
              ['Δ 1D', stockChangePct != null ? `${stockChangePct >= 0 ? '+' : ''}${stockChangePct.toFixed(2)}%` : '—', stockChangePct != null && stockChangePct >= 0 ? GREEN : RED],
              ['INTERVAL', ({ '5m': '5 MIN', '15m': '15 MIN', '1h': 'HOURLY', '1d': 'DAILY' } as Record<string, string>)[chartInterval] ?? chartInterval.toUpperCase(), T_AMBER],
              ['PATTERN', patternLabel ? `${patternLabel}${patternGrade ? ` (${patternGrade.g})` : ''}` : '—', patternRow ? (patternRow.direction === 'bullish' ? GREEN : RED) : T_LABEL],
            ].map(([lbl, val, color], i) => (
              <Box key={lbl} sx={{ px: 2.75, borderRight: i < 4 ? `1px solid ${T_BORDER_DIM}` : 'none', '&:first-of-type': { pl: 0 } }}>
                <Typography sx={{ fontSize: 9.5, color: T_LABEL, letterSpacing: '0.1em', mb: 0.5 }}>{lbl}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color, fontFamily: MONO }}>{val}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ py: 1.75, px: 2, borderBottom: `1px solid ${T_BORDER}` }}>
            <Typography sx={{ fontSize: 12, color: T_LABEL }}>No symbol selected — search a ticker below.</Typography>
          </Box>
        )
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {/* ── Left: chart column ─────────────────────────────────────── */}
        <Box sx={{ flex: '1 1 700px', minWidth: 0, p: 2 }}>
          {mode === 'pair' ? (
            <>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
                <Autocomplete
                  freeSolo
                  options={searchAResults}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.symbol} - ${option.name}`)}
                  inputValue={symbolAInput}
                  onInputChange={(_, value) => setSymbolAInput(value)}
                  onChange={(_, value) => { const s = typeof value === 'string' ? value : value?.symbol; if (s) setSymbolA(s.toUpperCase()); }}
                  sx={{ width: 220, ...termFieldSx }}
                  renderInput={(params) => <TextField {...params} size="small" label="Symbol A" placeholder="e.g. NBIS" />}
                  renderOption={(props, option) => (
                    <li {...props} key={option.symbol} style={{ fontFamily: MONO, fontSize: 12.5 }}>
                      <Box><Typography variant="body2" fontWeight="bold" sx={{ fontFamily: MONO, fontSize: 12.5 }}>{option.symbol}</Typography>
                        <Typography variant="caption" sx={{ color: T_LABEL }}>{option.name}</Typography></Box>
                    </li>
                  )}
                />
                <Typography sx={{ color: T_LABEL, alignSelf: 'center', pt: 1, fontSize: 12 }}>VS</Typography>
                <Autocomplete
                  freeSolo
                  options={searchBResults}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.symbol} - ${option.name}`)}
                  inputValue={symbolBInput}
                  onInputChange={(_, value) => setSymbolBInput(value)}
                  onChange={(_, value) => { const s = typeof value === 'string' ? value : value?.symbol; if (s) setSymbolB(s.toUpperCase()); }}
                  sx={{ width: 220, ...termFieldSx }}
                  renderInput={(params) => <TextField {...params} size="small" label="Symbol B" placeholder="e.g. NVDA" />}
                  renderOption={(props, option) => (
                    <li {...props} key={option.symbol} style={{ fontFamily: MONO, fontSize: 12.5 }}>
                      <Box><Typography variant="body2" fontWeight="bold" sx={{ fontFamily: MONO, fontSize: 12.5 }}>{option.symbol}</Typography>
                        <Typography variant="caption" sx={{ color: T_LABEL }}>{option.name}</Typography></Box>
                    </li>
                  )}
                />
                <ToggleButtonGroup value={rsTimeframe} exclusive onChange={(_, value) => value && setRsTimeframe(value)} size="small" sx={termToggleGroupSx}>
                  {RS_TIMEFRAMES.map((tf) => <ToggleButton key={tf.value} value={tf.value}>{tf.label}</ToggleButton>)}
                </ToggleButtonGroup>
                <Button
                  onClick={handlePlotPair} disabled={rsLoading}
                  sx={{
                    height: 40, borderRadius: 0, fontFamily: MONO, fontSize: 12, fontWeight: 700,
                    bgcolor: T_AMBER, color: '#fff', px: 2.5, '&:hover': { bgcolor: T_AMBER, opacity: 0.85 },
                    '&.Mui-disabled': { bgcolor: T_BORDER, color: T_LABEL },
                  }}
                >
                  {rsLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'PLOT'}
                </Button>
                <IconButton
                  onClick={handleSavePair} disabled={!!isPairSaved || savingPair || !rsData}
                  title={isPairSaved ? 'Already saved' : 'Save this pair'}
                  sx={{ ...termIconBtnSx, height: 40, width: 40 }}
                >
                  {savingPair ? <CircularProgress size={16} /> : <BookmarkAddIcon fontSize="small" sx={{ color: isPairSaved ? T_BORDER : T_AMBER }} />}
                </IconButton>
              </Box>
              {rsError && (
                <Box sx={{ mb: 2, px: 1.5, py: 1, border: `1px solid ${RED}`, bgcolor: RED_BG }}>
                  <Typography sx={{ fontSize: 12, color: RED, fontFamily: MONO }}>{rsError}</Typography>
                </Box>
              )}

              {rsData && rsData.series?.length > 0 ? (
                <Box sx={{ border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL }}>
                  <div ref={rsChartRef} style={{ width: '100%', height: 460 }} />
                  {rsData.annotations?.levels?.length === 0 && rsData.annotations?.trendlines?.length === 0 && rsData.annotations?.channels?.length === 0 && (
                    <Typography sx={{ fontSize: 11, color: T_LABEL, display: 'block', px: 1.5, py: 1, borderTop: `1px solid ${T_BORDER_DIM}` }}>
                      No support/resistance structure detected for this window — often means
                      one side has moved relentlessly with little back-and-forth to chart.
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL, textAlign: 'center', py: 10 }}>
                  <Typography sx={{ fontSize: 12, color: T_LABEL }}>Pick two symbols and hit PLOT to chart the ratio.</Typography>
                </Box>
              )}
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  size="small" placeholder="Search ticker (e.g. AAPL)" value={chartSearchValue}
                  onChange={(e) => setChartSearchValue(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleChartSearch(); }}
                  sx={{ width: 200, ...termFieldSx }}
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleChartSearch} disabled={!chartSearchValue.trim()} sx={{ color: T_AMBER }}><AddIcon fontSize="small" /></IconButton>
                    </InputAdornment>
                  ) }}
                />
                {selectedSymbol && (
                  <Chip
                    label={companyName ? `${companyName} (${selectedSymbol})` : selectedSymbol} size="small"
                    onDelete={() => { setSelectedSymbol(null); setCompanyName(null); setChartSearchValue(''); }}
                    sx={{ borderRadius: 0, bgcolor: T_AMBER_DIM, color: T_AMBER, fontFamily: MONO, '& .MuiChip-deleteIcon': { color: T_AMBER } }}
                  />
                )}
                <TextField
                  size="small" placeholder="Compare ticker" value={comparisonInput}
                  onChange={(e) => setComparisonInput(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleAddComparison(); }}
                  sx={{ width: 150, ...termFieldSx }}
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleAddComparison} disabled={!comparisonInput.trim()} sx={{ color: T_AMBER }}><AddIcon fontSize="small" /></IconButton>
                    </InputAdornment>
                  ) }}
                />
                {comparisonSymbols.map((symbol) => (
                  <Chip key={symbol} label={comparisonNames.get(symbol) ? `${comparisonNames.get(symbol)} (${symbol})` : symbol}
                    size="small" onDelete={() => handleRemoveComparison(symbol)}
                    sx={{ borderRadius: 0, bgcolor: T_PANEL, border: `1px solid ${T_BORDER}`, color: T_TEXT, fontFamily: MONO, '& .MuiChip-deleteIcon': { color: T_LABEL } }} />
                ))}
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup value={chartPeriod} exclusive onChange={handlePeriodChange} size="small" sx={termToggleGroupSx}>
                  <ToggleButton value="1d">1D</ToggleButton>
                  <ToggleButton value="5d">5D</ToggleButton>
                  <ToggleButton value="1mo">1M</ToggleButton>
                  <ToggleButton value="3mo">3M</ToggleButton>
                  <ToggleButton value="6mo">6M</ToggleButton>
                  <ToggleButton value="1y">1Y</ToggleButton>
                  <ToggleButton value="2y">2Y</ToggleButton>
                  <ToggleButton value="5y">5Y</ToggleButton>
                </ToggleButtonGroup>
                <IconButton onClick={() => mainChartApiRef.current?.timeScale().fitContent()} title="Reset zoom" sx={{ ...termIconBtnSx, height: 32, width: 32 }}>
                  <ZoomOutMapIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                <FormControlLabel control={<Checkbox checked={showMA20} onChange={(e) => setShowMA20(e.target.checked)} size="small" sx={{ color: T_BORDER, p: 0.5, '&.Mui-checked': { color: '#ff9800' } }} />}
                  label={<Typography sx={{ fontSize: 11, color: '#ff9800', fontFamily: MONO }}>20MA</Typography>} />
                <FormControlLabel control={<Checkbox checked={showMA50} onChange={(e) => setShowMA50(e.target.checked)} size="small" sx={{ color: T_BORDER, p: 0.5, '&.Mui-checked': { color: '#f44336' } }} />}
                  label={<Typography sx={{ fontSize: 11, color: '#f44336', fontFamily: MONO }}>50MA</Typography>} />
                <FormControlLabel control={<Checkbox checked={showMA200} onChange={(e) => setShowMA200(e.target.checked)} size="small" sx={{ color: T_BORDER, p: 0.5, '&.Mui-checked': { color: '#3b6ea5' } }} />}
                  label={<Typography sx={{ fontSize: 11, color: '#3b6ea5', fontFamily: MONO }}>200MA</Typography>} />
                <FormControlLabel control={<Checkbox checked={showBB} onChange={(e) => setShowBB(e.target.checked)} size="small" sx={{ color: T_BORDER, p: 0.5, '&.Mui-checked': { color: T_AMBER } }} />}
                  label={<Typography sx={{ fontSize: 11, color: T_AMBER, fontFamily: MONO }}>BBANDS</Typography>} />
                <FormControlLabel control={<Checkbox checked={showElliott} onChange={(e) => setShowElliott(e.target.checked)} size="small" sx={{ color: T_BORDER, p: 0.5, '&.Mui-checked': { color: '#22d3ee' } }} />}
                  label={<Typography sx={{ fontSize: 11, color: '#22d3ee', fontFamily: MONO }}>ELLIOTT</Typography>} />
                <FormControlLabel control={<Checkbox checked={showMACD} onChange={(e) => setShowMACD(e.target.checked)} size="small" sx={{ color: T_BORDER, p: 0.5, '&.Mui-checked': { color: '#60a5fa' } }} />}
                  label={<Typography sx={{ fontSize: 11, color: '#60a5fa', fontFamily: MONO }}>MACD</Typography>} />
                <FormControlLabel control={<Checkbox checked={showStochastic} onChange={(e) => setShowStochastic(e.target.checked)} size="small" sx={{ color: T_BORDER, p: 0.5, '&.Mui-checked': { color: '#c084fc' } }} />}
                  label={<Typography sx={{ fontSize: 11, color: '#c084fc', fontFamily: MONO }}>STOCH</Typography>} />
                <FormControlLabel control={<Checkbox checked={showVWAP} onChange={(e) => setShowVWAP(e.target.checked)} size="small" sx={{ color: T_BORDER, p: 0.5, '&.Mui-checked': { color: '#22c55e' } }} />}
                  label={<Typography sx={{ fontSize: 11, color: '#22c55e', fontFamily: MONO }}>VWAP</Typography>} />
                <FormControlLabel control={<Checkbox checked={showOBV} onChange={(e) => setShowOBV(e.target.checked)} size="small" sx={{ color: T_BORDER, p: 0.5, '&.Mui-checked': { color: '#ff9800' } }} />}
                  label={<Typography sx={{ fontSize: 11, color: '#ff9800', fontFamily: MONO }}>OBV</Typography>} />
                <FormControlLabel control={<Checkbox checked={showSupportResistance} onChange={(e) => setShowSupportResistance(e.target.checked)} size="small" sx={{ color: T_BORDER, p: 0.5, '&.Mui-checked': { color: '#d4a574' } }} />}
                  label={<Typography sx={{ fontSize: 11, color: '#d4a574', fontFamily: MONO }}>S/R</Typography>} />
              </Box>

              {hoverInfo && (
                <Typography sx={{ fontSize: 11, color: T_LABEL, mb: 1 }}>
                  {formatChartLabel(hoverInfo.date)} · O <Box component="span" sx={{ color: T_TEXT }}>{hoverInfo.open?.toFixed(2)}</Box> H <Box component="span" sx={{ color: T_TEXT }}>{hoverInfo.high?.toFixed(2)}</Box> L <Box component="span" sx={{ color: T_TEXT }}>{hoverInfo.low?.toFixed(2)}</Box> C <Box component="span" sx={{ color: T_TEXT }}>{hoverInfo.price?.toFixed(2)}</Box>
                </Typography>
              )}

              {chartLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL }}><CircularProgress sx={{ color: T_AMBER }} /></Box>
              ) : !selectedSymbol ? (
                <Box sx={{ textAlign: 'center', py: 8, border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL }}>
                  <Typography sx={{ fontSize: 12, color: T_LABEL, mb: 0.5 }}>Search for any stock ticker above to view the chart</Typography>
                  <Typography sx={{ fontSize: 11, color: T_BORDER }}>Enter a ticker symbol (e.g., AAPL, TSLA, MSFT) and press Enter or click +</Typography>
                </Box>
              ) : chartData.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL }}><Typography sx={{ fontSize: 12, color: T_LABEL }}>No chart data available for {selectedSymbol}</Typography></Box>
              ) : (
                <Box sx={{ border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL }}>
                  <div ref={mainChartContainerRef} style={{ width: '100%', height: 400 }} />
                </Box>
              )}

              {chartData.length > 0 && chartData.some((d) => d.rsi !== null) && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: 10, color: T_LABEL, letterSpacing: '0.08em', mb: 0.5 }}>RSI (14-DAY)</Typography>
                  <Box sx={{ border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL }}>
                    <div ref={rsiContainerRef} style={{ width: '100%', height: 120 }} />
                  </Box>
                </Box>
              )}

              {showMACD && chartData.length > 0 && chartData.some((d) => d.macd !== null) && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: 10, color: T_LABEL, letterSpacing: '0.08em', mb: 0.5 }}>MACD (12, 26, 9)</Typography>
                  <Box sx={{ border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL }}>
                    <div ref={macdContainerRef} style={{ width: '100%', height: 150 }} />
                  </Box>
                </Box>
              )}

              {showStochastic && chartData.length > 0 && chartData.some((d) => d.stoch_k !== null) && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: 10, color: T_LABEL, letterSpacing: '0.08em', mb: 0.5 }}>STOCHASTIC (14, 3, 3)</Typography>
                  <Box sx={{ border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL }}>
                    <div ref={stochContainerRef} style={{ width: '100%', height: 150 }} />
                  </Box>
                </Box>
              )}

              {showOBV && chartData.length > 0 && chartData.some((d) => d.obv !== null) && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: 10, color: T_LABEL, letterSpacing: '0.08em', mb: 0.5 }}>OBV — ON-BALANCE VOLUME</Typography>
                  <Box sx={{ border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL }}>
                    <div ref={obvContainerRef} style={{ width: '100%', height: 150 }} />
                  </Box>
                </Box>
              )}

              {showElliott && elliottWaves.length > 0 && chartData.length > 0 && (
                <Box sx={{ mt: 1.5, p: 1.5, border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL }}>
                  <Typography sx={{ fontSize: 11, color: '#22d3ee', mb: 1, letterSpacing: '0.05em' }}>ELLIOTT WAVE ANALYSIS</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#22d3ee' }}>Impulse Waves (1-5): Trend direction</Typography>
                    <Typography sx={{ fontSize: 11, color: '#ff5722' }}>Corrective Waves (A-C): Counter-trend</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11, color: T_LABEL, display: 'block', mt: 0.5 }}>
                    Wave points detected: {elliottWaves.map((w) => w.label).join(' → ')}
                  </Typography>
                </Box>
              )}

              {technicalEvents.length > 0 && (
                <Box sx={{ mt: 1.5, border: `1px solid ${T_BORDER}`, bgcolor: T_PANEL }}>
                  <Typography sx={{ fontSize: 10, color: T_LABEL, letterSpacing: '0.08em', p: 1.5, pb: 1, borderBottom: `1px solid ${T_BORDER_DIM}` }}>
                    KEY TECHNICAL EVENTS (LAST 12 MONTHS)
                  </Typography>
                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {technicalEvents.map((event, index) => {
                      const isGoldenCross = event.type === 'golden_cross';
                      const isDeathCross = event.type === 'death_cross';
                      const isMajorEvent = isGoldenCross || isDeathCross;
                      return (
                        <Box key={`${event.date}-${event.type}-${index}`} sx={{
                          display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1, px: 1.5,
                          bgcolor: isGoldenCross ? GREEN_BG : isDeathCross ? RED_BG : 'transparent',
                          borderBottom: index < technicalEvents.length - 1 ? `1px solid ${T_BORDER_DIM}` : 'none',
                        }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: event.sentiment === 'bullish' ? GREEN : RED, mt: 0.7, flexShrink: 0 }} />
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: isGoldenCross ? GREEN : isDeathCross ? RED : T_TEXT }}>
                                {isMajorEvent ? (isGoldenCross ? '⭐ ' : '⚠️ ') : ''}{event.title}
                              </Typography>
                              <Typography sx={{ fontSize: 10, color: event.sentiment === 'bullish' ? GREEN : RED }}>
                                {event.sentiment === 'bullish' ? '↑ BULLISH' : '↓ BEARISH'}
                              </Typography>
                              <Typography sx={{ fontSize: 10, color: T_LABEL }}>
                                {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 11, color: T_LABEL }}>{event.description}</Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* ── Right: saved pairs + watchlist panel ────────────────────── */}
        <Box sx={{ width: 340, flexShrink: 0, borderLeft: `1px solid ${T_BORDER}` }}>
          <Typography sx={{ fontSize: 10, color: T_LABEL, letterSpacing: '0.12em', px: 1.75, pt: 1.5, pb: 1, borderBottom: `1px solid ${T_BORDER_DIM}` }}>
            SAVED PAIRS ({savedPairs.length})
          </Typography>
          {pairsLoading ? (
            <Box sx={{ p: 1.75 }}><CircularProgress size={16} sx={{ color: T_AMBER }} /></Box>
          ) : savedPairs.length === 0 ? (
            <Typography sx={{ fontSize: 11, color: T_LABEL, p: 1.75 }}>
              No saved pairs yet — plot one in Pair mode and save it for quick access.
            </Typography>
          ) : (
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {savedPairs.map((pair) => {
                  const qa = quotes[pair.symbol_a];
                  const qb = quotes[pair.symbol_b];
                  const ratio = qa?.price != null && qb?.price ? qa.price / qb.price : null;
                  const ratioChangePct = qa?.change_pct != null && qb?.change_pct != null
                    ? ((1 + qa.change_pct / 100) / (1 + qb.change_pct / 100) - 1) * 100
                    : null;
                  const selected = mode === 'pair' && rsData && rsData.symbol_a === pair.symbol_a && rsData.symbol_b === pair.symbol_b;
                  return (
                    <Box component="tr" key={pair.id}
                      onClick={() => handleSelectSavedPair(pair)}
                      sx={{
                        cursor: 'pointer', bgcolor: selected ? T_ROW_SELECTED : 'transparent',
                        '&:hover': { bgcolor: selected ? T_ROW_SELECTED : T_ROW_HOVER },
                        '& td': { borderBottom: `1px solid ${T_BORDER_DIM}`, py: 0.9, fontSize: 11.5 },
                      }}
                    >
                      <Box component="td" sx={{ pl: 1.75, color: selected ? T_AMBER : T_TEXT, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {selected ? '▸ ' : ''}{pair.symbol_a}/{pair.symbol_b}
                      </Box>
                      <Box component="td" sx={{ textAlign: 'right', color: T_TEXT }}>{ratio != null ? ratio.toFixed(ratio >= 10 ? 2 : 4) : '—'}</Box>
                      <Box component="td" sx={{ textAlign: 'right', color: ratioChangePct == null ? T_LABEL : ratioChangePct >= 0 ? GREEN : RED }}>
                        {ratioChangePct != null ? `${ratioChangePct >= 0 ? '+' : ''}${ratioChangePct.toFixed(2)}%` : '—'}
                      </Box>
                      <Box component="td" sx={{ pr: 1, textAlign: 'right', width: 20 }}>
                        <IconButton size="small" onClick={(e) => handleDeletePair(pair.id, e)} sx={{ p: 0.25, color: T_LABEL, '&:hover': { color: RED } }}>
                          <DeleteIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </tbody>
            </Box>
          )}

          <Typography sx={{ fontSize: 10, color: T_LABEL, letterSpacing: '0.12em', px: 1.75, pt: 2, pb: 1, borderTop: `1px solid ${T_BORDER}`, borderBottom: `1px solid ${T_BORDER_DIM}` }}>
            WATCHLIST ({watchlist.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, px: 1.75, py: 1.25, borderBottom: `1px solid ${T_BORDER_DIM}` }}>
            <TextField
              size="small" fullWidth placeholder="Add ticker" value={watchlistInput}
              onChange={(e) => setWatchlistInput(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleAddToWatchlist(); }}
              sx={{ ...termFieldSx, '& .MuiOutlinedInput-root': { ...termFieldSx['& .MuiOutlinedInput-root'], fontSize: 11 } }}
            />
            <IconButton size="small" onClick={() => handleAddToWatchlist()} disabled={!watchlistInput.trim()} sx={{ ...termIconBtnSx, height: 32, width: 32 }}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          {watchlist.length === 0 ? (
            <Typography sx={{ fontSize: 11, color: T_LABEL, p: 1.75 }}>No stocks added yet.</Typography>
          ) : (
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {watchlist.map((ticker) => {
                  const q = quotes[ticker];
                  const selected = mode === 'stock' && selectedSymbol === ticker;
                  return (
                    <Box component="tr" key={ticker}
                      onClick={() => handleSelectWatchlistStock(ticker)}
                      sx={{
                        cursor: 'pointer', bgcolor: selected ? T_ROW_SELECTED : 'transparent',
                        '&:hover': { bgcolor: selected ? T_ROW_SELECTED : T_ROW_HOVER },
                        '& td': { borderBottom: `1px solid ${T_BORDER_DIM}`, py: 0.9, fontSize: 11.5 },
                      }}
                    >
                      <Box component="td" sx={{ pl: 1.75, color: selected ? T_AMBER : T_TEXT, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {selected ? '▸ ' : ''}{ticker}
                      </Box>
                      <Box component="td" sx={{ textAlign: 'right', color: T_TEXT }}>
                        {q?.price != null ? formatCurrencyPrice(q.price, getCurrencyFromTicker(ticker)) : '—'}
                      </Box>
                      <Box component="td" sx={{ textAlign: 'right', color: q?.change_pct == null ? T_LABEL : q.change_pct >= 0 ? GREEN : RED }}>
                        {q?.change_pct != null ? `${q.change_pct >= 0 ? '+' : ''}${q.change_pct.toFixed(2)}%` : '—'}
                      </Box>
                      <Box component="td" sx={{ pr: 1, textAlign: 'right', width: 20 }}>
                        <IconButton size="small" onClick={(e) => handleRemoveFromWatchlist(ticker, e)} sx={{ p: 0.25, color: T_LABEL, '&:hover': { color: RED } }}>
                          <DeleteIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </tbody>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Charting;
